
import { cleanupExpiredItems } from '@/utils/clientCacheUtils';
import { EMBEDDING_STORE, RESPONSE_STORE, MESSAGES_STORE, CACHE_STATS_STORE, CACHE_RELATIONSHIPS_STORE } from './cache-constants';
import { getCachedItem, setCachedItem, getCacheKeys } from '@/utils/clientCacheUtils';
import { detectDeviceCapabilities } from './device-detection';
import type { DeviceCapabilities } from './device-detection';
import { getAllCacheMetrics } from './cache-metrics';
import type { CacheMetrics } from './cache-metrics';

// Enhanced function to clean up expired items with memory usage optimization and device capability awareness
export const cleanupClientCache = async (): Promise<void> => {
  try {
    // Standard cleanup of expired items
    await Promise.all([
      cleanupExpiredItems(EMBEDDING_STORE),
      cleanupExpiredItems(RESPONSE_STORE),
      cleanupExpiredItems(MESSAGES_STORE),
      cleanupExpiredItems(CACHE_STATS_STORE),
      cleanupExpiredItems(CACHE_RELATIONSHIPS_STORE)
    ]);
    
    // Enhanced device capability detection
    const deviceCapabilities = await detectDeviceCapabilities();
    
    // If device has limited memory, be more aggressive in cleanup
    if (deviceCapabilities.hasLimitedMemory) {
      console.log(`Low memory device detected (${deviceCapabilities.availableMemoryMB}MB), performing aggressive cache cleanup`);
      
      // Get current cache metrics to determine what to clean up
      const metrics = await getAllCacheMetrics();
      
      // Clean up low-value items with enhanced pruning logic
      for (const store of Object.keys(metrics)) {
        await pruneCache(store, deviceCapabilities);
      }
    }
  } catch (error) {
    console.error('Error cleaning up client cache:', error);
  }
};

// Enhanced pruning with more sophisticated algorithms
async function pruneCache(storeName: string, deviceCapabilities: DeviceCapabilities): Promise<number> {
  try {
    // Adaptive cache size limits based on device capabilities
    const maxItems = {
      [EMBEDDING_STORE]: deviceCapabilities.hasLimitedMemory ? 30 : 100,
      [RESPONSE_STORE]: deviceCapabilities.hasLimitedMemory ? 50 : 200,
      [MESSAGES_STORE]: deviceCapabilities.hasLimitedMemory ? 3 : 10,
    };
    
    const limit = maxItems[storeName as keyof typeof maxItems] || 100;
    
    // Get metrics to check item count
    const metrics = await getCachedItem<CacheMetrics>(`metrics_${storeName}`, CACHE_STATS_STORE);
    const itemCount = metrics?.sizeMetrics?.itemCount || 0;
    
    if (itemCount <= limit) {
      return 0; // No pruning needed
    }
    
    console.log(`Pruning ${storeName} cache: ${itemCount} items exceed limit of ${limit}`);
    
    // Get all keys and sort by relevance/popularity
    const keys = await getCacheKeys(storeName);
    let prunedCount = 0;
    
    // Get all items to assess their value
    const itemsToAssess: { key: string; value: any }[] = [];
    for (const key of keys) {
      const item = await getCachedItem(key, storeName);
      if (item) {
        itemsToAssess.push({ key, value: item });
      }
    }
    
    // Sort items by their value/popularity (if available)
    itemsToAssess.sort((a, b) => {
      const aPopularity = a.value.popularity || 0;
      const bPopularity = b.value.popularity || 0;
      return aPopularity - bPopularity; // Least popular first
    });
    
    // Keep only the most valuable items
    const itemsToRemove = itemsToAssess.slice(0, itemCount - limit);
    
    // Remove the least valuable items
    for (const item of itemsToRemove) {
      await cleanupExpiredItems(storeName, [item.key]);
      prunedCount++;
    }
    
    console.log(`Pruned ${prunedCount} items from ${storeName} cache`);
    return prunedCount;
  } catch (error) {
    console.error(`Error pruning cache for ${storeName}:`, error);
    return 0;
  }
}

// Enhanced optimization with recommendations and content relationships
export const optimizeClientCache = async (): Promise<void> => {
  try {
    console.log('Cache optimization started');
    
    // Get cache metrics
    const metrics = await getAllCacheMetrics();
    
    // Enhanced cleanup with device awareness
    const deviceCapabilities = await detectDeviceCapabilities();
    if (deviceCapabilities.hasLimitedMemory) {
      console.log('Low memory device detected, performing targeted optimization');
    }
    
    // Run cleanup
    await cleanupClientCache();
    
    // Analyze cache effectiveness with enhanced insights
    const recommendations: string[] = [];
    
    // Analyze hit rates with more detail
    for (const [store, storeMetrics] of Object.entries(metrics)) {
      const hitRate = storeMetrics.hits / (storeMetrics.hits + storeMetrics.misses || 1);
      
      if (hitRate < 0.3) {
        recommendations.push(`Low hit rate (${(hitRate * 100).toFixed(1)}%) for ${store} cache. Consider adjusting TTL or caching strategy.`);
      }
      
      // More detailed language analysis
      if (storeMetrics.byLanguage) {
        for (const [lang, langMetrics] of Object.entries(storeMetrics.byLanguage)) {
          const langHitRate = langMetrics.hits / (langMetrics.hits + langMetrics.misses || 1);
          if (langHitRate < 0.2) {
            recommendations.push(`Very low hit rate (${(langHitRate * 100).toFixed(1)}%) for ${lang} in ${store} cache.`);
          }
        }
      }
      
      // Topic-specific recommendations
      if (storeMetrics.byTopic) {
        for (const [topic, topicMetrics] of Object.entries(storeMetrics.byTopic)) {
          const topicHitRate = topicMetrics.hits / (topicMetrics.hits + topicMetrics.misses || 1);
          if (topicHitRate < 0.2) {
            recommendations.push(`Low hit rate for topic "${topic}". Consider pre-caching common queries.`);
          }
        }
      }
    }
    
    // Add device-specific recommendations
    if (deviceCapabilities.hasLimitedMemory) {
      recommendations.push(`Limited memory device detected (${deviceCapabilities.availableMemoryMB}MB available). Consider reducing max cache size.`);
    }
    
    if (deviceCapabilities.isMobile && !deviceCapabilities.supportsCompressionAPI) {
      recommendations.push('Mobile device without compression API detected. Consider using smaller embedding models for better performance.');
    }
    
    // Store recommendations for the admin UI
    await setCachedItem(
      'cache_optimization_recommendations',
      { recommendations, timestamp: new Date() },
      CACHE_STATS_STORE,
      { maxAge: 24 * 60 * 60 * 1000 } // 24 hours
    );
    
    console.log('Cache optimization completed with recommendations:', recommendations);
  } catch (error) {
    console.error('Error optimizing client cache:', error);
  }
};

// Get cache optimization recommendations
export const getCacheOptimizationRecommendations = async (): Promise<string[]> => {
  try {
    const data = await getCachedItem<{ recommendations: string[] }>(
      'cache_optimization_recommendations', 
      CACHE_STATS_STORE
    );
    return data?.recommendations || [];
  } catch (error) {
    console.error('Error getting cache recommendations:', error);
    return [];
  }
};
